import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';

const PAGE_SIZE = 10;
const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: 'M12 5v14 M5 12h14',
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
    x: 'M18 6L6 18 M6 6l12 12',
    refresh: 'M23 4v6h-6 M1 20v-6h6 M3.51 9a9 9 0 0114.85-3.36L23 10 M1 14l4.64 4.36A9 9 0 0020.49 15',
    truck: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    dollar: 'M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    chevronLeft: 'M15 18l-6-6 6-6',
    chevronRight: 'M9 18l6-6-6-6',
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
};

const STATUS_OPTIONS = [
    { value: 'Active', label: 'Active', color: '#166534', bg: '#f0fdf4', border: '#dcfce7', dot: '#22c55e', emoji: '✅' },
    { value: 'Inactive', label: 'Inactive', color: '#475569', bg: '#f1f5f9', border: '#e2e8f0', dot: '#94a3b8', emoji: '⊘' },
    { value: 'Under Maintenance', label: 'Under Maintenance', color: '#92400e', bg: '#fefce8', border: '#fef08a', dot: '#f59e0b', emoji: '🔧' },
    { value: 'Damaged', label: 'Damaged', color: '#991b1b', bg: '#fef2f2', border: '#fecaca', dot: '#ef4444', emoji: '⚠' },
    { value: 'Retired', label: 'Retired', color: '#374151', bg: '#f3f4f6', border: '#e5e7eb', dot: '#6b7280', emoji: '🗄' },
    { value: 'In Transit', label: 'In Transit', color: '#1e40af', bg: '#eff6ff', border: '#bfdbfe', dot: '#3b82f6', emoji: '🚛' },
];

const defaultForm = {
    plateNumber: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    vehicleType: '',
    fuelType: '',
    vehicleStatus: 'Active',
    color: '',
    purchasePrice: '',
    acquisitionDate: '',
    depreciationMonthly: '',
    gpsMonthly: '',
    dashcamMonthly: '',
    insuranceMonthly: '',
    otherCostsMonthly: '',
};

export default function AllVehicles({ user, operatorDetails }) {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [editingId, setEditingId] = useState(null);
    const [toast, setToast] = useState(null);
    const fileInputRef = useRef(null);
    const [importing, setImporting] = useState(false);

    // Master data from backend
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [fuelTypes, setFuelTypes] = useState([]);
    const [vehicleColors, setVehicleColors] = useState([]);
    const [vehicleStatuses, setVehicleStatuses] = useState(STATUS_OPTIONS);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchVehicles = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/vehicles/${user?.operatorId || user?._id || 'all'}`);
            setVehicles(res.data);
        } catch (err) {
            console.error('Fetch vehicles error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMasterData = async () => {
        try {
            const res = await axios.get(`${API}/master-data`);
            const all = res.data;
            setVehicleTypes(all.filter(i => i.category === 'vehicle-types' && i.active));
            setFuelTypes(all.filter(i => i.category === 'fuel-types' && i.active));

            // Map vehicle statuses
            const statuses = all.filter(i => i.category === 'vehicle-status' && i.active);
            if (statuses.length > 0) {
                setVehicleStatuses(statuses.map(s => ({
                    value: s.name,
                    label: s.name,
                    color: s.color || '#475569',
                    bg: `${s.color}15` || '#f1f5f9',
                    border: `${s.color}30` || '#e2e8f0',
                    dot: s.color || '#94a3b8'
                })));
            }

            // Use vehicle-status colors as a color palette
            const colors = all.filter(i => i.active && i.color);
            // Build a unique color palette from all master data colors
            const uniqueColors = [...new Set(colors.map(i => i.color))];
            setVehicleColors(uniqueColors.length > 0 ? uniqueColors : [
                '#4F6EF7', '#22C55E', '#EF4444', '#F59E0B', '#A855F7',
                '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#6B7280',
                '#1E293B', '#06B6D4', '#84CC16', '#DC2626', '#9333EA',
                '#3B82F6', '#FFFFFF', '#000000',
            ]);
        } catch (err) {
            console.error('Fetch master data error:', err);
        }
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Plate Number': 'DXB-1234',
                'Make': 'Toyota',
                'Model': 'Camry',
                'Year': 2022,
                'Vehicle Type': 'Sedan',
                'Fuel Type': 'Petrol',
                'Vehicle Status': 'Active',
                'Color': '#FFFFFF',
                'Purchase Price': 85000,
                'Acquisition Date': '2022-01-01',
                'Monthly Depreciation': 1000,
                'Monthly GPS Fee': 50,
                'Monthly Dashcam Fee': 30,
                'Monthly Insurance': 200,
                'Other Monthly Costs': 100
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Vehicles');
        XLSX.writeFile(wb, 'Vehicles_Template.xlsx');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImporting(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws);

                const mapped = data.map(row => ({
                    plateNumber: row['Plate Number'],
                    make: row['Make'],
                    model: row['Model'],
                    year: row['Year'],
                    vehicleType: row['Vehicle Type'],
                    fuelType: row['Fuel Type'],
                    vehicleStatus: row['Vehicle Status'] || 'Active',
                    color: row['Color'],
                    purchasePrice: row['Purchase Price'],
                    acquisitionDate: row['Acquisition Date'],
                    depreciationMonthly: row['Monthly Depreciation'],
                    gpsMonthly: row['Monthly GPS Fee'],
                    dashcamMonthly: row['Monthly Dashcam Fee'],
                    insuranceMonthly: row['Monthly Insurance'],
                    otherCostsMonthly: row['Other Monthly Costs']
                }));

                const res = await fetch('/api/vehicles/import', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ operatorId: user?.operatorId || user?._id, vehicles: mapped })
                });
                const result = await res.json();
                if (res.ok) {
                    showToast(`${result.successCount} vehicles imported!`);
                    fetchVehicles();
                } else {
                    showToast(result.message || 'Import failed', 'error');
                }
            } catch (err) {
                showToast('Format error', 'error');
            } finally {
                setImporting(false);
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    useEffect(() => {
        fetchVehicles();
        fetchMasterData();
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return vehicles.filter(v => {
            return !q ||
                (v.plateNumber || '').toLowerCase().includes(q) ||
                (v.make || '').toLowerCase().includes(q) ||
                (v.model || '').toLowerCase().includes(q) ||
                (v.vehicleType || '').toLowerCase().includes(q) ||
                (v.fuelType || '').toLowerCase().includes(q) ||
                (v.color || '').toLowerCase().includes(q);
        });
    }, [vehicles, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                operatorId: user?.operatorId || user?._id,
                purchasePrice: parseFloat(form.purchasePrice) || 0,
                year: parseInt(form.year) || new Date().getFullYear(),
                depreciationMonthly: parseFloat(form.depreciationMonthly) || 0,
                gpsMonthly: parseFloat(form.gpsMonthly) || 0,
                dashcamMonthly: parseFloat(form.dashcamMonthly) || 0,
                insuranceMonthly: parseFloat(form.insuranceMonthly) || 0,
                otherCostsMonthly: parseFloat(form.otherCostsMonthly) || 0,
            };
            if (editingId) {
                await axios.put(`${API}/vehicles/${editingId}`, payload);
                showToast('Vehicle updated successfully');
            } else {
                await axios.post(`${API}/vehicles`, payload);
                showToast('Vehicle added successfully');
            }
            fetchVehicles();
            closeModal();
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to save vehicle';
            showToast(msg, 'error');
        }
    };

    const handleDelete = async (vehicle) => {
        if (!window.confirm(`Delete vehicle "${vehicle.plateNumber}"?`)) return;
        try {
            await axios.delete(`${API}/vehicles/${vehicle._id}`);
            showToast('Vehicle deleted successfully');
            fetchVehicles();
        } catch (err) {
            showToast('Failed to delete vehicle', 'error');
        }
    };

    const openModal = (vehicle = null) => {
        if (vehicle) {
            setEditingId(vehicle._id);
            setForm({
                plateNumber: vehicle.plateNumber || '',
                make: vehicle.make || '',
                model: vehicle.model || '',
                year: vehicle.year || new Date().getFullYear(),
                vehicleType: vehicle.vehicleType || '',
                fuelType: vehicle.fuelType || '',
                vehicleStatus: vehicle.vehicleStatus || 'Active',
                color: vehicle.color || '',
                purchasePrice: vehicle.purchasePrice?.toString() || '',
                acquisitionDate: vehicle.acquisitionDate ? vehicle.acquisitionDate.split('T')[0] : '',
                depreciationMonthly: vehicle.depreciationMonthly?.toString() || '',
                gpsMonthly: vehicle.gpsMonthly?.toString() || '',
                dashcamMonthly: vehicle.dashcamMonthly?.toString() || '',
                insuranceMonthly: vehicle.insuranceMonthly?.toString() || '',
                otherCostsMonthly: vehicle.otherCostsMonthly?.toString() || '',
            });
        } else {
            setEditingId(null);
            setForm(defaultForm);
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(defaultForm);
    };

    const getStatusStyle = (status) => {
        const opt = vehicleStatuses.find(s => s.value === status);
        if (!opt) return {};
        return { background: opt.bg, color: opt.color, border: `1px solid ${opt.border}` };
    };

    const getStatusDot = (status) => {
        const opt = vehicleStatuses.find(s => s.value === status);
        return opt?.dot || '#94a3b8';
    };

    return (
        <div className="av-page">
            <style>{`
                .av-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }

                /* Header */
                .av-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                .av-title { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
                .av-subtitle { font-size: 14px; color: #64748b; margin: 0; }
                .av-add-btn {
                    background: #1e1b4b; color: white; border: none; padding: 11px 22px;
                    border-radius: 10px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    transition: all 0.2s; box-shadow: 0 4px 12px rgba(30, 27, 75, 0.3); font-size: 14px;
                }
                .av-add-btn:hover { background: #312e81; transform: translateY(-1px); }

                /* Premium Buttons */
                .premium-btn {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 24px;
                    border-radius: 14px;
                    font-weight: 700;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    white-space: nowrap;
                }
                .premium-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                }
                .premium-btn:active {
                    transform: translateY(0);
                }
                .btn-import {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                }
                .btn-template {
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                }
                .btn-primary {
                    background: #1e1b4b;
                    box-shadow: 0 4px 12px rgba(30, 27, 75, 0.25);
                }
                .premium-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                /* Content Box */
                .av-content {
                    background: white; border-radius: 16px; border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03); overflow: hidden;
                }

                /* Toolbar */
                .av-toolbar {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 18px 24px; border-bottom: 1px solid #f1f5f9;
                }
                .av-toolbar-left { display: flex; align-items: center; gap: 10px; }
                .av-toolbar-left h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0; }
                .av-search-wrap {
                    position: relative; display: flex; align-items: center;
                }
                .av-search-wrap svg { position: absolute; left: 12px; color: #94a3b8; }
                .av-search {
                    padding: 9px 12px 9px 36px; border: 1.5px solid #e2e8f0; border-radius: 10px;
                    font-size: 14px; color: #1e293b; background: #f8fafc; outline: none;
                    width: 260px; transition: all 0.2s; font-family: 'Inter', sans-serif;
                }
                .av-search:focus { border-color: #1e1b4b; box-shadow: 0 0 0 3px rgba(30, 27, 75, 0.08); }
                .av-refresh-btn {
                    border: 1.5px solid #e2e8f0; background: white; padding: 8px; border-radius: 10px;
                    cursor: pointer; color: #64748b; transition: all 0.2s; display: flex; align-items: center;
                }
                .av-refresh-btn:hover { border-color: #1e1b4b; color: #1e1b4b; }

                /* Table */
                .av-table-wrap { overflow-x: auto; }
                .av-table { width: 100%; border-collapse: collapse; }
                .av-table th {
                    padding: 14px 20px; text-align: left; font-size: 12px; font-weight: 600;
                    color: #64748b; text-transform: uppercase; letter-spacing: 0.05em;
                    border-bottom: 1px solid #e2e8f0; background: #fafbfc;
                }
                .av-table td { padding: 14px 20px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
                .av-table tr:hover { background: #fafbfc; }
                .av-table tr:last-child td { border-bottom: none; }

                /* Vehicle Info Cell */
                .av-vehicle-info { display: flex; align-items: center; gap: 12px; }
                .av-color-dot {
                    width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
                    border: 2px solid rgba(0,0,0,0.08);
                }
                .av-vehicle-details { display: flex; flex-direction: column; }
                .av-vehicle-plate { font-weight: 700; color: #1e293b; font-size: 14px; }
                .av-vehicle-makemodel { font-size: 13px; color: #64748b; }

                /* Type Cell */
                .av-type-badge {
                    display: inline-flex; align-items: center; gap: 4px; padding: 4px 12px;
                    border-radius: 6px; font-size: 12px; font-weight: 500; background: #f1f5f9; color: #475569;
                }

                /* Financials Cell */
                .av-financial { display: flex; flex-direction: column; }
                .av-price { font-weight: 600; color: #1e293b; font-size: 14px; }
                .av-costs { font-size: 12px; color: #64748b; }

                /* Status */
                .av-status-badge {
                    padding: 5px 14px; border-radius: 99px; font-size: 11px; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .av-status-dot { width: 7px; height: 7px; border-radius: 50%; }

                /* Actions */
                .av-actions { display: flex; gap: 6px; }
                .av-act-btn {
                    border: none; background: none; padding: 7px; cursor: pointer; border-radius: 8px;
                    color: #94a3b8; transition: all 0.2s; display: flex; align-items: center;
                }
                .av-act-btn:hover { background: #f1f5f9; color: #1e1b4b; }
                .av-act-btn.delete:hover { background: #fee2e2; color: #ef4444; }

                /* Pagination */
                .av-pagination {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 14px 24px; border-top: 1px solid #f1f5f9;
                }
                .av-page-info { font-size: 13px; color: #64748b; }
                .av-page-btns { display: flex; gap: 4px; }
                .av-page-btn {
                    border: 1px solid #e2e8f0; background: white; padding: 6px 12px;
                    border-radius: 8px; cursor: pointer; font-size: 13px; color: #64748b;
                    transition: all 0.15s; display: flex; align-items: center; gap: 4px;
                }
                .av-page-btn:hover:not(:disabled) { border-color: #1e1b4b; color: #1e1b4b; }
                .av-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
                .av-page-btn.active { background: #1e1b4b; color: white; border-color: #1e1b4b; }

                /* Empty */
                .av-empty { text-align: center; padding: 60px 20px; color: #94a3b8; font-size: 14px; }

                /* ===== MODAL REDESIGN ===== */
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes modalPop { from { transform: scale(0.95) translateY(24px); opacity: 0; } to { transform: scale(1) translateY(0); opacity: 1; } }

                .av-modal-overlay {
                    position: fixed; inset: 0; background: rgba(8, 14, 30, 0.55);
                    backdrop-filter: blur(16px); display: flex; align-items: center; justify-content: center;
                    z-index: 1000; animation: fadeIn 0.25s ease; padding: 20px;
                }

                .av-modal {
                    background: #ffffff; border-radius: 28px; width: 100%; max-width: 820px;
                    box-shadow: 0 40px 80px -20px rgba(8, 14, 30, 0.3), 0 0 0 1px rgba(255,255,255,0.08);
                    max-height: 92vh; overflow: hidden; display: flex; flex-direction: column;
                    animation: modalPop 0.35s cubic-bezier(0.22, 1, 0.36, 1);
                }

                /* --- Modal Header (clean white) --- */
                .vm-header {
                    background: #ffffff;
                    padding: 24px 32px 0; flex-shrink: 0; position: relative;
                    border-bottom: 1px solid #f1f5f9;
                }
                .vm-header-top {
                    display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px;
                }
                .vm-header-left { display: flex; align-items: center; gap: 14px; }
                .vm-header-icon {
                    width: 46px; height: 46px; border-radius: 14px;
                    background: linear-gradient(135deg, #6366f1, #4f46e5); display: flex; align-items: center;
                    justify-content: center; box-shadow: 0 4px 12px rgba(99,102,241,0.35);
                }
                .vm-header-title { color: #0f172a; font-size: 20px; font-weight: 800; letter-spacing: -0.02em; margin: 0 0 3px; }
                .vm-header-sub { color: #94a3b8; font-size: 13px; font-weight: 500; margin: 0; }
                .vm-close-btn {
                    border: none; background: #f1f5f9; cursor: pointer;
                    color: #64748b; padding: 9px; border-radius: 10px;
                    transition: all 0.2s; display: flex; align-items: center; flex-shrink: 0;
                }
                .vm-close-btn:hover { background: #fee2e2; color: #ef4444; transform: rotate(90deg); }

                /* --- Tabs --- */
                .vm-tabs { display: flex; gap: 2px; position: relative; }
                .vm-tab {
                    padding: 11px 20px; border: none; background: transparent; cursor: pointer;
                    color: #94a3b8; font-size: 13px; font-weight: 700;
                    border-radius: 10px 10px 0 0; transition: all 0.2s; display: flex; align-items: center; gap: 7px;
                    font-family: 'Inter', sans-serif; letter-spacing: 0.01em; border-bottom: 2px solid transparent;
                }
                .vm-tab:hover { color: #475569; background: #f8fafc; }
                .vm-tab.active {
                    background: #f8fafc; color: #4f46e5; font-weight: 800;
                    border-bottom: 2px solid #6366f1;
                }
                .vm-tab-dot {
                    width: 7px; height: 7px; border-radius: 50%; background: currentColor; opacity: 0.7;
                }

                /* --- Modal Body --- */
                .vm-body { padding: 32px; overflow-y: auto; flex: 1; }

                /* --- Form Field Groups --- */
                .vm-grid { display: grid; gap: 20px; }
                .vm-grid-2 { grid-template-columns: 1fr 1fr; }
                .vm-grid-3 { grid-template-columns: 1fr 1fr 1fr; }

                .vm-field { display: flex; flex-direction: column; gap: 7px; }
                .vm-field.span2 { grid-column: span 2; }

                .vm-label {
                    font-size: 12px; font-weight: 700; color: #64748b;
                    text-transform: uppercase; letter-spacing: 0.06em; display: flex; align-items: center; gap: 5px;
                }
                .vm-label .req { color: #f43f5e; }

                .vm-input-wrap { position: relative; }
                .vm-input-ico { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }

                .vm-input, .vm-select {
                    width: 100%; padding: 13px 14px 13px 42px; border: 1.5px solid #e2e8f0;
                    border-radius: 12px; font-size: 14px; font-weight: 600; color: #0f172a;
                    background: #f8fafc; outline: none; transition: all 0.2s;
                    font-family: 'Inter', sans-serif; box-sizing: border-box;
                }
                .vm-input:focus, .vm-select:focus {
                    border-color: #6366f1; background: #fff;
                    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
                }
                .vm-input:focus ~ .vm-input-ico, .vm-input-wrap:focus-within .vm-input-ico { color: #6366f1; }
                .vm-input::placeholder { color: #cbd5e1; font-weight: 400; }
                .vm-input.no-icon, .vm-select.no-icon { padding-left: 14px; }

                .vm-select {
                    appearance: none;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
                    background-repeat: no-repeat; background-position: right 14px center; background-size: 16px;
                    padding-right: 40px;
                }

                /* Currency badge on inputs */
                .vm-money-wrap { position: relative; }
                .vm-money-badge {
                    position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
                    font-size: 10px; font-weight: 800; color: #6366f1; background: #eef2ff;
                    padding: 3px 8px; border-radius: 6px; letter-spacing: 0.04em; pointer-events: none;
                }
                .vm-money-input { padding-right: 70px !important; }

                /* --- Status Picker --- */
                .vm-status-wrap { position: relative; }
                .vm-status-btn {
                    width: 100%; padding: 13px 14px; border: 1.5px solid #e2e8f0; border-radius: 12px;
                    background: #f8fafc; cursor: pointer; display: flex; align-items: center; gap: 10px;
                    transition: all 0.2s; font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; color: #0f172a;
                }
                .vm-status-btn:hover { border-color: #6366f1; background: #fff; }
                .vm-status-pill { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
                .vm-status-arrow { margin-left: auto; color: #94a3b8; font-size: 11px; }
                .vm-status-dropdown {
                    position: absolute; top: calc(100% + 8px); left: 0; right: 0; z-index: 200;
                    background: white; border-radius: 16px; border: 1px solid #e2e8f0;
                    box-shadow: 0 20px 40px -10px rgba(0,0,0,0.15); padding: 8px;
                    animation: menuPop 0.25s cubic-bezier(0.22, 1, 0.36, 1);
                }
                @keyframes menuPop { from { opacity: 0; transform: translateY(-8px) scale(0.97); } to { opacity: 1; transform: none; } }
                .vm-status-opt {
                    display: flex; align-items: center; gap: 10px; padding: 11px 14px;
                    border-radius: 10px; cursor: pointer; transition: all 0.15s; font-size: 13px; font-weight: 600; color: #334155;
                }
                .vm-status-opt:hover { background: #f1f5f9; }
                .vm-status-opt.active { background: #eef2ff; color: #4f46e5; }

                /* --- Color Picker --- */
                .vm-color-panel { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 16px; padding: 18px; }
                .vm-color-swatches { display: flex; flex-wrap: wrap; gap: 9px; margin-bottom: 14px; }
                .vm-swatch {
                    width: 32px; height: 32px; border-radius: 8px; cursor: pointer; border: 2.5px solid white;
                    box-shadow: 0 0 0 1px #d1d5db; transition: all 0.18s cubic-bezier(0.34,1.56,0.64,1); position: relative;
                }
                .vm-swatch:hover { transform: scale(1.25) translateY(-2px); box-shadow: 0 6px 14px rgba(0,0,0,0.15), 0 0 0 1px #94a3b8; z-index: 2; }
                .vm-swatch.active { box-shadow: 0 0 0 3px #6366f1; transform: scale(1.15); z-index: 2; }
                .vm-swatch.active::after { content: '✓'; position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: white; font-size: 13px; font-weight: 900; text-shadow: 0 1px 3px rgba(0,0,0,0.4); }
                .vm-color-custom { display: flex; align-items: center; gap: 12px; padding-top: 14px; border-top: 1px solid #e2e8f0; }
                .vm-colorpicker-preview { width: 40px; height: 40px; border-radius: 10px; border: 2.5px solid white; box-shadow: 0 0 0 1px #d1d5db; flex-shrink: 0; position: relative; overflow: hidden; }
                .vm-colorpicker-preview input { position: absolute; inset: -8px; width: 140%; height: 140%; cursor: pointer; border: none; padding: 0; }
                .vm-hex-field { padding: 8px 12px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 13px; font-weight: 700; font-family: monospace; color: #1e293b; width: 100px; outline: none; background: white; transition: border-color 0.2s; }
                .vm-hex-field:focus { border-color: #6366f1; }

                /* --- Section divider --- */
                .vm-section-label {
                    font-size: 11px; font-weight: 800; color: #94a3b8; text-transform: uppercase;
                    letter-spacing: 0.1em; margin-bottom: 16px; display: flex; align-items: center; gap: 10px;
                }
                .vm-section-label::after { content: ''; flex: 1; height: 1px; background: #e2e8f0; }

                /* --- Footer --- */
                .vm-footer {
                    padding: 20px 32px; border-top: 1px solid #f1f5f9; display: flex;
                    justify-content: space-between; align-items: center; flex-shrink: 0;
                    background: rgba(255,255,255,0.95); backdrop-filter: blur(10px);
                }
                .vm-footer-hint { font-size: 12px; color: #94a3b8; font-weight: 500; }
                .vm-footer-actions { display: flex; gap: 12px; }
                .vm-btn-cancel {
                    padding: 12px 24px; border: 1.5px solid #e2e8f0; background: white;
                    border-radius: 12px; font-size: 13px; font-weight: 700; color: #64748b;
                    cursor: pointer; transition: all 0.2s; font-family: 'Inter', sans-serif;
                }
                .vm-btn-cancel:hover { border-color: #94a3b8; color: #1e293b; background: #f8fafc; }
                .vm-btn-save {
                    padding: 12px 32px; border: none; background: linear-gradient(135deg, #6366f1, #4f46e5);
                    color: white; border-radius: 12px; font-size: 13px; font-weight: 800;
                    cursor: pointer; transition: all 0.25s; font-family: 'Inter', sans-serif;
                    box-shadow: 0 8px 20px -4px rgba(99,102,241,0.45);
                }
                .vm-btn-save:hover { transform: translateY(-2px); box-shadow: 0 12px 28px -6px rgba(99,102,241,0.6); background: linear-gradient(135deg, #4f46e5, #3730a3); }
                .vm-btn-save:active { transform: translateY(0); }

                /* ===== TOAST ===== */
                .av-toast {
                    position: fixed; top: 24px; right: 24px; padding: 16px 28px; border-radius: 16px;
                    color: white; z-index: 2000; box-shadow: 0 20px 40px -8px rgba(0,0,0,0.25);
                    font-weight: 700; font-size: 14px; animation: toastSlide 0.4s cubic-bezier(0.22,1,0.36,1);
                    display: flex; align-items: center; gap: 10px;
                }
                @keyframes toastSlide { from { transform: translateX(110%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
                .av-toast.success { background: linear-gradient(135deg,#10b981,#059669); }
                .av-toast.error { background: linear-gradient(135deg,#f43f5e,#dc2626); }
            `}</style>

            {toast && <div className={`av-toast ${toast.type}`}>{toast.msg}</div>}

            {/* Header */}
            <div className="av-header">
                <div>
                    <h1 className="av-title">Fleet Management</h1>
                    <p className="av-subtitle">Manage your vehicle fleet and financial specifications</p>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <input type="file" ref={fileInputRef} hidden accept=".xlsx,.xls" onChange={handleImport} />
                    <button className="premium-btn btn-import" onClick={() => fileInputRef.current.click()} disabled={importing}>
                        <Icon d={icons.upload} size={18} strokeWidth={2.5} />
                        {importing ? 'Importing...' : 'Import'}
                    </button>
                    <button className="premium-btn btn-template" onClick={downloadTemplate}>
                        <Icon d={icons.download} size={18} strokeWidth={2.5} />
                        Template
                    </button>
                    <button className="premium-btn btn-primary" onClick={() => openModal()}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                        Add Vehicle
                    </button>
                </div>
            </div>

            {/* Content Box */}
            <div className="av-content">
                <div className="av-toolbar">
                    <div className="av-toolbar-left">
                        <h3>Vehicles List</h3>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="av-search-wrap">
                            <Icon d={icons.search} size={16} />
                            <input
                                className="av-search"
                                placeholder="Search vehicles..."
                                value={search}
                                onChange={e => { setSearch(e.target.value); setPage(1); }}
                            />
                        </div>
                        <button className="av-refresh-btn" onClick={fetchVehicles} title="Refresh">
                            <Icon d={icons.refresh} size={16} />
                        </button>
                    </div>
                </div>

                <div className="av-table-wrap">
                    <table className="av-table">
                        <thead>
                            <tr>
                                <th>Vehicle Info</th>
                                <th>Type</th>
                                <th>Financials</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" className="av-empty">Loading vehicles...</td></tr>
                            ) : pageData.length === 0 ? (
                                <tr><td colSpan="5" className="av-empty">No vehicles found</td></tr>
                            ) : (
                                pageData.map(v => (
                                    <tr key={v._id}>
                                        <td>
                                            <div className="av-vehicle-info">
                                                <div
                                                    className="av-color-dot"
                                                    style={{ background: v.color || '#e2e8f0' }}
                                                />
                                                <div className="av-vehicle-details">
                                                    <span className="av-vehicle-plate">{v.plateNumber}</span>
                                                    <span className="av-vehicle-makemodel">
                                                        {v.make} {v.model} {v.year ? `(${v.year})` : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                {v.vehicleType && <span className="av-type-badge">{v.vehicleType}</span>}
                                                {v.fuelType && <span className="av-type-badge" style={{ background: '#fef3c7', color: '#92400e' }}>{v.fuelType}</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="av-financial">
                                                <span className="av-price">{formatCurrency(v.purchasePrice, operatorDetails?.country)}</span>
                                                <span className="av-costs">
                                                    Depr: {formatCurrency(v.depreciationMonthly, operatorDetails?.country)} | Ins: {formatCurrency(v.insuranceMonthly, operatorDetails?.country)}
                                                </span>
                                                <span className="av-costs">
                                                    GPS: {formatCurrency(v.gpsMonthly, operatorDetails?.country)} | Dash: {formatCurrency(v.dashcamMonthly, operatorDetails?.country)} | Other: {formatCurrency(v.otherCostsMonthly, operatorDetails?.country)}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="av-status-badge" style={getStatusStyle(v.vehicleStatus)}>
                                                <span className="av-status-dot" style={{ background: getStatusDot(v.vehicleStatus) }}></span>
                                                {v.vehicleStatus}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="av-actions">
                                                <button className="av-act-btn" title="Edit" onClick={() => openModal(v)}>
                                                    <Icon d={icons.edit} size={16} />
                                                </button>
                                                <button className="av-act-btn delete" title="Delete" onClick={() => handleDelete(v)}>
                                                    <Icon d={icons.trash} size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {filtered.length > PAGE_SIZE && (
                    <div className="av-pagination">
                        <span className="av-page-info">
                            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                        </span>
                        <div className="av-page-btns">
                            <button className="av-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                                <Icon d={icons.chevronLeft} size={14} /> Prev
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
                                Math.max(0, page - 3), Math.min(totalPages, page + 2)
                            ).map(p => (
                                <button key={p} className={`av-page-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)}>
                                    {p}
                                </button>
                            ))}
                            <button className="av-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                                Next <Icon d={icons.chevronRight} size={14} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal */}
            {
                showModal && (
                    <VehicleModal
                        editingId={editingId}
                        form={form}
                        setForm={setForm}
                        vehicleTypes={vehicleTypes}
                        fuelTypes={fuelTypes}
                        vehicleColors={vehicleColors}
                        vehicleStatuses={vehicleStatuses}
                        onClose={closeModal}
                        onSave={handleSave}
                        operatorDetails={operatorDetails}
                    />
                )
            }
        </div >
    );
}

/* ── Vehicle Modal Component ── */
function VehicleModal({ editingId, form, setForm, vehicleTypes, fuelTypes, vehicleColors, vehicleStatuses, onClose, onSave, operatorDetails }) {
    const [statusOpen, setStatusOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('details');

    const selectedStatus = vehicleStatuses.find(s => s.value === form.vehicleStatus) || vehicleStatuses[0] || {};

    const inputIcons = {
        plate: 'M13 16V6a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 00 2 2h7m-6 0a2 2 0 100-4 2 2 0 000 4zm12 0a2 2 0 100-4 2 2 0 000 4zm3-9v6h-3',
        tag: 'M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z M7 7h.01',
        hash: 'M4 9h16 M4 15h16 M10 3L8 21 M16 3l-2 18',
        calendar: 'M8 7V3 M16 7V3 M3 11h18 M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z',
        money: 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6',
    };

    const currencyCode = operatorDetails?.defaultCurrency?.split('(')[1]?.replace(')', '') || 'AED';

    return (
        <div className="av-modal-overlay" onClick={onClose}>
            <div className="av-modal" onClick={e => e.stopPropagation()}>

                {/* ── Gradient Header with Tabs ── */}
                <div className="vm-header">
                    <div className="vm-header-top">
                        <div className="vm-header-left">
                            <div className="vm-header-icon">
                                <Icon d={icons.truck} size={24} color="white" />
                            </div>
                            <div>
                                <h2 className="vm-header-title">{editingId ? 'Edit Vehicle Profile' : 'Register New Vehicle'}</h2>
                                <p className="vm-header-sub">{editingId ? 'Update the vehicle details below' : 'Fill in details to add a new vehicle'}</p>
                            </div>
                        </div>
                        <button className="vm-close-btn" onClick={onClose} title="Close">
                            <Icon d={icons.x} size={20} color="white" />
                        </button>
                    </div>
                    <div className="vm-tabs">
                        <button type="button" className={`vm-tab${activeTab === 'details' ? ' active' : ''}`} onClick={() => setActiveTab('details')}>
                            <span className="vm-tab-dot" />
                            Vehicle Details
                        </button>
                        <button type="button" className={`vm-tab${activeTab === 'financials' ? ' active' : ''}`} onClick={() => setActiveTab('financials')}>
                            <span className="vm-tab-dot" />
                            Financials
                        </button>
                    </div>
                </div>

                <form onSubmit={onSave} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

                    {/* ── Tab: Vehicle Details ── */}
                    {activeTab === 'details' && (
                        <div className="vm-body">

                            <div className="vm-section-label">Core Information</div>
                            <div className="vm-grid vm-grid-2" style={{ marginBottom: 24 }}>

                                <div className="vm-field">
                                    <label className="vm-label">Plate Number <span className="req">*</span></label>
                                    <div className="vm-input-wrap">
                                        <Icon d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" size={16} color="#94a3b8" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
                                        <input className="vm-input" required placeholder="e.g. DXB-1234" value={form.plateNumber} onChange={e => setForm({ ...form, plateNumber: e.target.value })} />
                                    </div>
                                </div>

                                <div className="vm-field">
                                    <label className="vm-label">Manufacturing Year</label>
                                    <div className="vm-input-wrap">
                                        <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} color="#94a3b8" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
                                        <input className="vm-input" type="number" placeholder={new Date().getFullYear().toString()} value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} />
                                    </div>
                                </div>

                                <div className="vm-field">
                                    <label className="vm-label">Make / Manufacturer <span className="req">*</span></label>
                                    <div className="vm-input-wrap">
                                        <Icon d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" size={16} color="#94a3b8" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
                                        <input className="vm-input" required placeholder="e.g. Toyota" value={form.make} onChange={e => setForm({ ...form, make: e.target.value })} />
                                    </div>
                                </div>

                                <div className="vm-field">
                                    <label className="vm-label">Model Name</label>
                                    <div className="vm-input-wrap">
                                        <Icon d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" size={16} color="#94a3b8" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
                                        <input className="vm-input" placeholder="e.g. Camry" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} />
                                    </div>
                                </div>

                                <div className="vm-field">
                                    <label className="vm-label">Vehicle Category</label>
                                    <div className="vm-input-wrap">
                                        <Icon d={icons.truck[2]} size={16} color="#94a3b8" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
                                        <select className="vm-select" value={form.vehicleType} onChange={e => setForm({ ...form, vehicleType: e.target.value })}>
                                            <option value="">Select Category...</option>
                                            {vehicleTypes.map(vt => <option key={vt._id} value={vt.name}>{vt.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <div className="vm-field">
                                    <label className="vm-label">Fuel Type</label>
                                    <div className="vm-input-wrap">
                                        <Icon d="M3 2v10M3 2h18M21 2v10M3 12h18" size={16} color="#94a3b8" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
                                        <select className="vm-select" value={form.fuelType} onChange={e => setForm({ ...form, fuelType: e.target.value })}>
                                            <option value="">Select Fuel Type...</option>
                                            {fuelTypes.map(ft => <option key={ft._id} value={ft.name}>{ft.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                            </div>

                            <div className="vm-section-label" style={{ marginBottom: 16 }}>Operational Status</div>
                            <div className="vm-status-wrap" style={{ marginBottom: 28 }}>
                                <button type="button" className="vm-status-btn" onClick={() => setStatusOpen(!statusOpen)}>
                                    <span className="vm-status-pill" style={{ background: selectedStatus.dot }} />
                                    <span style={{ fontWeight: 700 }}>{selectedStatus.label || 'Select Status'}</span>
                                    <span className="vm-status-arrow">▾</span>
                                </button>
                                {statusOpen && (
                                    <div className="vm-status-dropdown">
                                        {vehicleStatuses.map(opt => (
                                            <div key={opt.value} className={`vm-status-opt${form.vehicleStatus === opt.value ? ' active' : ''}`}
                                                onClick={() => { setForm({ ...form, vehicleStatus: opt.value }); setStatusOpen(false); }}>
                                                <span className="vm-status-pill" style={{ background: opt.dot }} />
                                                {opt.label}
                                                {form.vehicleStatus === opt.value && <span style={{ marginLeft: 'auto', color: '#6366f1' }}>✓</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="vm-section-label" style={{ marginBottom: 16 }}>Vehicle Color</div>
                            <div className="vm-color-panel">
                                <div className="vm-color-swatches">
                                    {vehicleColors.slice(0, 18).map(c => (
                                        <div key={c} className={`vm-swatch${form.color === c ? ' active' : ''}`}
                                            style={{ background: c }} onClick={() => setForm({ ...form, color: c })} title={c} />
                                    ))}
                                </div>
                                <div className="vm-color-custom">
                                    <div className="vm-colorpicker-preview" style={{ background: form.color || '#ffffff' }}>
                                        <input type="color" value={form.color || '#ffffff'} onChange={e => setForm({ ...form, color: e.target.value.toUpperCase() })} />
                                    </div>
                                    <input className="vm-hex-field" placeholder="#FFFFFF" value={form.color}
                                        onChange={e => setForm({ ...form, color: e.target.value.toUpperCase() })} />
                                    <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>or enter hex</span>
                                </div>
                            </div>

                        </div>
                    )}

                    {/* ── Tab: Financials ── */}
                    {activeTab === 'financials' && (
                        <div className="vm-body">

                            <div className="vm-section-label">Asset Valuation</div>
                            <div className="vm-grid vm-grid-2" style={{ marginBottom: 28 }}>

                                <div className="vm-field">
                                    <label className="vm-label">Purchase Price</label>
                                    <div className="vm-money-wrap">
                                        <input className="vm-input no-icon vm-money-input" type="number" step="0.01" placeholder="0.00" value={form.purchasePrice} onChange={e => setForm({ ...form, purchasePrice: e.target.value })} />
                                        <span className="vm-money-badge">{currencyCode}</span>
                                    </div>
                                </div>

                                <div className="vm-field">
                                    <label className="vm-label">Acquisition Date</label>
                                    <div className="vm-input-wrap">
                                        <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} color="#94a3b8" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
                                        <input className="vm-input" type="date" value={form.acquisitionDate} onChange={e => setForm({ ...form, acquisitionDate: e.target.value })} />
                                    </div>
                                </div>

                            </div>

                            <div className="vm-section-label">Monthly Fixed Costs</div>
                            <div className="vm-grid vm-grid-3">

                                {[
                                    { label: 'Depreciation', key: 'depreciationMonthly' },
                                    { label: 'GPS Fee', key: 'gpsMonthly' },
                                    { label: 'Dashcam Fee', key: 'dashcamMonthly' },
                                    { label: 'Insurance', key: 'insuranceMonthly' },
                                    { label: 'Other Costs', key: 'otherCostsMonthly' },
                                ].map(({ label, key }) => (
                                    <div className="vm-field" key={key}>
                                        <label className="vm-label">{label}</label>
                                        <div className="vm-money-wrap">
                                            <input className="vm-input no-icon vm-money-input" type="number" step="0.01" placeholder="0.00"
                                                value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} />
                                            <span className="vm-money-badge">{currencyCode}</span>
                                        </div>
                                    </div>
                                ))}

                                {/* Monthly total badge */}
                                <div className="vm-field" style={{ justifyContent: 'flex-end' }}>
                                    <label className="vm-label">Monthly Total</label>
                                    <div style={{
                                        padding: '13px 16px', borderRadius: 12, background: 'linear-gradient(135deg,#eef2ff,#e0e7ff)',
                                        border: '1.5px solid #c7d2fe', fontWeight: 800, fontSize: 18, color: '#4338ca', textAlign: 'center'
                                    }}>
                                        {currencyCode} {(
                                            (parseFloat(form.depreciationMonthly) || 0) +
                                            (parseFloat(form.gpsMonthly) || 0) +
                                            (parseFloat(form.dashcamMonthly) || 0) +
                                            (parseFloat(form.insuranceMonthly) || 0) +
                                            (parseFloat(form.otherCostsMonthly) || 0)
                                        ).toFixed(2)}
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}

                    {/* ── Footer ── */}
                    <div className="vm-footer">
                        <span className="vm-footer-hint">
                            {activeTab === 'details' ? '⬅ Fill vehicle info, then check Financials tab' : '✓ All fixed monthly costs are included in cost analysis'}
                        </span>
                        <div className="vm-footer-actions">
                            <button type="button" className="vm-btn-cancel" onClick={onClose}>Cancel</button>
                            <button type="submit" className="vm-btn-save">
                                {editingId ? '✓ Update Vehicle' : '+ Save Vehicle'}
                            </button>
                        </div>
                    </div>

                </form>
            </div>
        </div>
    );
}
